import {
  type ApolloClient,
  type DocumentNode,
  type TypedDocumentNode,
  type OperationVariables,
} from '@apollo/client';
import { ExportStatus } from '../hooks/useExportFile';

const DEFAULT_DELAY = 2000;
const DEFAULT_MAX_BACKOFF = 30000;

type PollOptions<TQuery, TQueryVariables extends OperationVariables> = {
  client: ApolloClient<object>;
  queryDocument: DocumentNode | TypedDocumentNode<TQuery, TQueryVariables>;
  variables: TQueryVariables;
  extractGetFileData: (
    data: TQuery,
  ) => { status: ExportStatus } & Record<string, unknown>;
  maxRetries: number;
  delayTimeBetweenAttempts?: number;
  useExponentialBackoff?: boolean;
  maxBackoffDelay?: number;
  onCompleted: (data: TQuery) => void;
  onFailed: (error: Error) => void;
  signal: AbortSignal;
};

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

export async function pollExportFile<
  TQuery,
  TQueryVariables extends OperationVariables,
>(options: PollOptions<TQuery, TQueryVariables>): Promise<void> {
  const {
    client,
    queryDocument,
    variables,
    extractGetFileData,
    maxRetries,
    delayTimeBetweenAttempts = DEFAULT_DELAY,
    useExponentialBackoff = true,
    maxBackoffDelay = DEFAULT_MAX_BACKOFF,
    onCompleted,
    onFailed,
    signal,
  } = options;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (signal.aborted) {
      return;
    }

    try {
      const result = await client.query<TQuery, TQueryVariables>({
        query: queryDocument,
        variables,
        fetchPolicy: 'network-only',
      });

      const fileData = extractGetFileData(result.data);

      if (fileData.status === ExportStatus.Failed) {
        onFailed(new Error('Failed to get exported file. Please try again'));
        return;
      }

      if (fileData.status === ExportStatus.Success) {
        onCompleted(result.data);
        return;
      }

      // Still Processing — wait before next attempt
      const delay = useExponentialBackoff
        ? Math.min(
            delayTimeBetweenAttempts * Math.pow(2, attempt),
            maxBackoffDelay,
          )
        : delayTimeBetweenAttempts;

      await wait(delay, signal);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      onFailed(
        err instanceof Error
          ? err
          : new Error('Failed to get exported file. Please try again'),
      );
      return;
    }
  }

  // Exceeded max retries
  onFailed(new Error('Failed to get exported file. Please try again'));
}
